/**
 * Mission store — the single client-side source of truth for the open mission.
 *
 *  bundle      configuration (GET /missions/:id), refreshed on `bundle_changed`
 *  record      live MissionRecord: GET record, then realtime messages folded in (sorted, capped)
 *  connection  MissionSocket status
 *  selection   shared map/list selection
 *
 * Selectors: useMission(s => s.record?.alerts) etc. `rev` increments on every record change.
 */
import { create } from 'zustand';
import type { AircraftSourcesStatus, Alert, Broadcast, EpochMs, Id, MissionBundle, MissionRecord, ServerMessage } from '@apos/shared';
import { api, errorMessage, type WeatherResponse } from '../lib/api';
import { MissionSocket, type SocketStatus } from '../lib/ws';
import type { SelectionRef } from '../lib/types';
import {
  applyBackfill,
  applyMessages,
  DEFAULT_TELEMETRY_CAP_MS,
  emptyRecord,
  newestTelemetryT,
  normaliseRecord,
  syncRecordConfig,
} from './recordReducer';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface MissionState {
  missionId: Id | null;
  status: LoadStatus;
  error: string | null;
  bundle: MissionBundle | null;
  record: MissionRecord | null;
  /** increments on every record mutation */
  rev: number;
  connection: SocketStatus;
  /** newest telemetry t folded in (backfill bookmark) */
  lastSeenT: EpochMs | null;
  weather: WeatherResponse | null;
  selection: SelectionRef | null;
  followAircraftId: Id | null;
  /** Public-source status per aircraft (GET aircraft/:id/sources + WS `aircraft_sources`). */
  aircraftSources: Record<Id, AircraftSourcesStatus>;

  load(missionId: Id): Promise<void>;
  unload(): void;
  refreshBundle(): Promise<void>;
  reloadRecord(): Promise<void>;
  loadWeather(): Promise<void>;
  setBundle(bundle: MissionBundle): void;
  select(sel: SelectionRef | null): void;
  follow(aircraftId: Id | null): void;
  reconnect(): void;
  /** Exposed for tests & sims: fold messages immediately. */
  ingest(msgs: ServerMessage[]): void;
}

type BroadcastListener = (broadcasts: Broadcast[]) => void;
const broadcastListeners = new Set<BroadcastListener>();
/** Subscribe to broadcasts that arrive over the socket (first sighting only). */
export function onNewBroadcasts(fn: BroadcastListener): () => void {
  broadcastListeners.add(fn);
  return () => broadcastListeners.delete(fn);
}

type PlanListener = (missionId: Id, revision: number) => void;
const planListeners = new Set<PlanListener>();
/** Schedule & shots changed on the server (another device saved, or crew ticked a shot). */
export function onPlanChanged(fn: PlanListener): () => void {
  planListeners.add(fn);
  return () => planListeners.delete(fn);
}

type AlertListener = (alerts: Alert[]) => void;
const alertListeners = new Set<AlertListener>();
/** Subscribe to alerts that arrive over the socket (not the initial load). */
export function onNewAlerts(fn: AlertListener): () => void {
  alertListeners.add(fn);
  return () => alertListeners.delete(fn);
}

let socket: MissionSocket | null = null;
let queue: ServerMessage[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let bundleTimer: ReturnType<typeof setTimeout> | null = null;
let loadSeq = 0;
let recoveryTimer: ReturnType<typeof setInterval> | null = null;
let recoveryListener: (()=>void) | null = null;
let recovering = false;
const FLUSH_MS = 60;

export const useMission = create<MissionState>((set, get) => {
  const flush = () => {
    flushTimer = null;
    const msgs = queue;
    queue = [];
    if (msgs.length) get().ingest(msgs);
  };

  const enqueue = (msg: ServerMessage) => {
    if (msg.type === 'pong' || msg.type === 'hello') return;
    if (msg.type === 'plan') {
      const mid = get().missionId;
      if (mid) for (const l of planListeners) l(mid, msg.revision);
      return;
    }
    queue.push(msg);
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  };

  const scheduleBundleRefresh = () => {
    if (bundleTimer) clearTimeout(bundleTimer);
    bundleTimer = setTimeout(() => {
      bundleTimer = null;
      void get().refreshBundle();
    }, 250);
  };

  const backfill = async (refreshConfig=true) => {
    if(recovering)return;
    const { missionId, lastSeenT } = get();
    if (!missionId) return;
    const seq = loadSeq;
    recovering=true;
    try {
      if (lastSeenT === null) {
        await get().reloadRecord();
        return;
      }
      // small overlap; reducer dedupes on t
      const data = await api.telemetrySince(missionId, lastSeenT - 2000);
      if (seq !== loadSeq) return;
      const rec = get().record;
      if (!rec) return;
      const res = applyBackfill(rec, data, DEFAULT_TELEMETRY_CAP_MS);
      if (res.record !== rec) {
        set((s) => ({
          record: res.record,
          rev: s.rev + 1,
          lastSeenT: res.newestTelemetryT !== null ? Math.max(res.newestTelemetryT, s.lastSeenT ?? 0) : s.lastSeenT,
        }));
      }
      // markers/alerts/media may also have changed while away
      if(refreshConfig)void get().refreshBundle();
    } catch {
      /* Periodic recovery and reconnect both retry without creating provider requests. */
    } finally {recovering=false;}
  };

  const loadSources = async (missionId: Id, bundle: MissionBundle, seq: number) => {
    const out: Record<Id, AircraftSourcesStatus> = {};
    await Promise.all(
      bundle.aircraft.map(async (a) => {
        try {
          out[a.id] = await api.aircraftSources(missionId, a.id);
        } catch {
          /* best effort: older servers / offline */
        }
      }),
    );
    if (seq !== loadSeq) return;
    set((s) => ({ aircraftSources: { ...out, ...s.aircraftSources } }));
  };

  return {
    missionId: null,
    status: 'idle',
    error: null,
    bundle: null,
    record: null,
    rev: 0,
    connection: 'idle',
    lastSeenT: null,
    weather: null,
    selection: null,
    followAircraftId: null,
    aircraftSources: {},

    async load(missionId) {
      if (get().missionId === missionId && get().status !== 'error') return;
      get().unload();
      const seq = ++loadSeq;
      set({ missionId, status: 'loading', error: null });
      try {
        const bundle = await api.missions.get(missionId);
        if (seq !== loadSeq) return;
        set({ bundle, record: emptyRecord(bundle), rev: get().rev + 1 });
        // socket first so nothing is missed between record fetch and subscribe
        socket = new MissionSocket({
          missionId,
          onMessage: enqueue,
          onStatus: (s) => set({ connection: s }),
          onReconnect: () => void backfill(),
        });
        socket.start();
        recoveryListener=()=>{if(document.visibilityState==='visible')void backfill();};
        document.addEventListener('visibilitychange',recoveryListener);
        window.addEventListener('online',recoveryListener);
        recoveryTimer=setInterval(()=>{if(document.visibilityState==='visible')void backfill(false);},15_000);
        let rec: MissionRecord;
        let recError: string | null = null;
        try {
          rec = normaliseRecord(await api.record(missionId));
        } catch (e) {
          // configuration still renders without telemetry
          rec = emptyRecord(bundle);
          recError = errorMessage(e);
        }
        if (seq !== loadSeq) return;
        // fold anything that arrived while the record was in flight
        const pending = queue;
        queue = [];
        const merged = pending.length ? applyMessages(syncRecordConfig(rec, bundle), pending).record : syncRecordConfig(rec, bundle);
        set((s) => ({ record: merged, rev: s.rev + 1, lastSeenT: newestTelemetryT(merged), status: 'ready', error: recError }));
        void loadSources(missionId, bundle, seq);
      } catch (e) {
        if (seq !== loadSeq) return;
        set({ status: 'error', error: errorMessage(e) });
      }
    },

    unload() {
      if(recoveryTimer)clearInterval(recoveryTimer);recoveryTimer=null;
      if(recoveryListener){document.removeEventListener('visibilitychange',recoveryListener);window.removeEventListener('online',recoveryListener);}recoveryListener=null;
      loadSeq++;
      socket?.stop();
      socket = null;
      queue = [];
      if (flushTimer) clearTimeout(flushTimer);
      if (bundleTimer) clearTimeout(bundleTimer);
      flushTimer = bundleTimer = null;
      set({
        missionId: null,
        status: 'idle',
        error: null,
        bundle: null,
        record: null,
        connection: 'idle',
        lastSeenT: null,
        weather: null,
        selection: null,
        followAircraftId: null,
        aircraftSources: {},
      });
    },

    async refreshBundle() {
      const id = get().missionId;
      if (!id) return;
      const seq = loadSeq;
      try {
        const [bundle, media, markers] = await Promise.all([
          api.missions.get(id),
          api.media.list(id).catch(() => null),
          api.markers.list(id).catch(() => null),
        ]);
        if (seq !== loadSeq) return;
        get().setBundle(bundle);
        const rec = get().record;
        if (rec && (media || markers)) {
          set((s) => ({
            record: {
              ...rec,
              mediaClips: media ? [...media].sort((a, b) => a.startT - b.startT) : rec.mediaClips,
              markers: markers ? [...markers].sort((a, b) => a.t - b.t) : rec.markers,
            },
            rev: s.rev + 1,
          }));
        }
      } catch {
        /* keep the last good bundle */
      }
    },

    async reloadRecord() {
      const id = get().missionId;
      const bundle = get().bundle;
      if (!id || !bundle) return;
      const seq = loadSeq;
      try {
        const rec = syncRecordConfig(normaliseRecord(await api.record(id)), get().bundle ?? bundle);
        if (seq !== loadSeq) return;
        set((s) => ({ record: rec, rev: s.rev + 1, lastSeenT: newestTelemetryT(rec) }));
      } catch (e) {
        set({ error: errorMessage(e) });
      }
    },

    async loadWeather() {
      const id = get().missionId;
      if (!id) return;
      try {
        const w = await api.weather(id);
        if (get().missionId === id) set({ weather: w });
      } catch {
        /* weather is best-effort */
      }
    },

    setBundle(bundle) {
      const rec = get().record;
      set((s) => ({ bundle, record: rec ? syncRecordConfig(rec, bundle) : emptyRecord(bundle), rev: s.rev + 1 }));
    },

    select(sel) {
      set({ selection: sel });
    },

    follow(aircraftId) {
      set({ followAircraftId: aircraftId });
    },

    reconnect() {
      socket?.reconnectNow();
    },

    ingest(msgs) {
      const rec = get().record;
      if (!rec || get().status !== 'ready') {
        // record still loading: hold messages; load() folds them in after the fetch
        queue.push(...msgs);
        return;
      }
      const res = applyMessages(rec, msgs, DEFAULT_TELEMETRY_CAP_MS);
      if (res.changed) {
        set((s) => {
          const bundle = s.bundle && res.record.mission !== s.bundle.mission ? { ...s.bundle, mission: res.record.mission } : s.bundle;
          return {
            record: res.record,
            bundle,
            rev: s.rev + 1,
            lastSeenT: res.newestTelemetryT !== null ? Math.max(res.newestTelemetryT, s.lastSeenT ?? 0) : s.lastSeenT,
          };
        });
      }
      if (res.bundleChanged.size) scheduleBundleRefresh();
      if (res.sources.length) {
        set((s) => {
          const next = { ...s.aircraftSources };
          for (const st of res.sources) next[st.aircraftId] = st;
          return { aircraftSources: next };
        });
      }
      if (res.newBroadcasts.length) for (const l of broadcastListeners) l(res.newBroadcasts);
      if (res.newAlerts.length) {
        // system notifications + toasts are coalesced by features/alerts/AlertLayer
        for (const l of alertListeners) l(res.newAlerts);
      }
    },
  };
});

/** Imperative accessor for non-React code. */
export const missionStore = useMission;
